import { useState } from 'react';
import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';
import type { ApiErrorDetail } from '@klareospace/pharos-lib-types';

import { IMPORT_TYPE_CONFIGS } from '../config';
import type { ImportType, ImportTypeConfig } from '../config';
import { useImportFile } from './use-import-file';

// ─── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_IMPORT_TYPE_CONFIG: ImportTypeConfig =
  IMPORT_TYPE_CONFIGS.find((config) => config.active) ?? IMPORT_TYPE_CONFIGS[0];

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseImportPageReturn {
  error: ApiErrorDetail | null;
  isUploading: boolean;
  onFileSelected: (file: File) => void;
  onTypeChange: (type: ImportType) => void;
  options: readonly ImportTypeConfig[];
  selectedConfig: ImportTypeConfig;
  selectedType: ImportType;
}

/**
 * Composes the import page's upload wiring:
 * - Owns the selected import type, defaulting to the first active entry in `IMPORT_TYPE_CONFIGS`.
 *   Switching type also resets the upload mutation so a stale error from the previous type does
 *   not survive the switch.
 * - Exposes the upload mutation's error as a plain `ApiErrorDetail` for the page to render in an
 *   `ErrorBanner` (the mutation itself does not toast on error — see `useImportFile`).
 */
export function useImportPage(): UseImportPageReturn {
  const [selectedConfig, setSelectedConfig] = useState<ImportTypeConfig>(DEFAULT_IMPORT_TYPE_CONFIG);
  const importFile = useImportFile(selectedConfig);

  function onFileSelected(file: File): void {
    importFile.mutate(file);
  }

  function onTypeChange(type: ImportType): void {
    const nextConfig = IMPORT_TYPE_CONFIGS.find((config) => config.type === type);
    if (!nextConfig) return;
    setSelectedConfig(nextConfig);
    importFile.reset();
  }

  return {
    error: importFile.error ? getApiErrorDetail(importFile.error) : null,
    isUploading: importFile.isPending,
    onFileSelected,
    onTypeChange,
    options: IMPORT_TYPE_CONFIGS,
    selectedConfig,
    selectedType: selectedConfig.type,
  };
}
